import { T } from '../styles/tokens';
import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { signal } from '@preact/signals';
import { tenantName } from '../components/TenantLogo';
import { api } from '../api/client';
import { isLoggedIn, hasActivePlan, isAdmin } from '../stores/auth.store';
import { NoPlanGate } from '../components/NoPlanGate';
import { SkeletonBar, ListSkeleton, Spinner, TypingIndicator, OnlineDot, IconButton, Button, Modal, Textarea, Input } from '../components/ui';
import { CHAT_COLORS, FILE_EXTENSION_COLORS, FILE_EXTENSION_DEFAULT_COLOR } from '../constants/chat';
import { toolMeta } from '../constants/tools';
import { renderContent } from '../utils/renderContent';
import { useChatSounds } from '../hooks/useChatSounds';
import { fileToDataUrl } from '../utils/files';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool_call' | 'tool';
  content: string;
  timestamp: string;
  attachments?: Array<{ type: string; mimeType: string; dataUrl: string }>;
  toolData?: Array<{ name: string; id?: string; summary?: string }>;
}

interface ChatMachine {
  id: string;
  name: string;
  agentName: string;
  agentModel?: string;
  status: 'online' | 'offline';
  os: string;
  lastSeen: string;
  hasOpenclaw?: boolean;
}

// ─── State ───────────────────────────────────────────────────────────────────

const messages = signal<ChatMessage[]>([]);
const inputText = signal('');
const loading = signal(true);
const sending = signal(false);
const lastMessageTs = signal('');
const dragOver = signal(false);
const dropModalImages = signal<Array<{ file: File; dataUrl: string }>>([]);
const dropModalText = signal('');
const attachMenuOpen = signal(false);
const dropModalDocs = signal<Array<{ file: File; dataUrl: string }>>([]);
const dropModalDocText = signal('');
const DOC_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.csv', '.xls', '.xlsx', '.json', '.md', '.zip', '.rar', '.xml', '.html', '.py', '.js', '.ts'];
const MAX_DOC_SIZE = 10 * 1024 * 1024;
const pendingImages = signal<Array<{ file: File; dataUrl: string }>>([]);
const pendingSendIds = signal<Set<string>>(new Set());
const showActions = signal(localStorage.getItem('chat_showActions') !== 'false');
const activityState = signal<{ status: string; detail?: string; agentName?: string; streamingText?: string; tool?: { name: string; detail: string } }>({ status: 'idle' });
const imageModal = signal<string | null>(null);
const deployStatus = signal<{ status: string; sha: string; message: string; createdAt: string; url: string } | null>(null);
const sendModal = signal(false);
const selectedMachine = signal<ChatMachine | null>(null);
const machineDropdownOpen = signal(false);
const machinesList = signal<ChatMachine[]>([]);
const machinesLoading = signal(true);

const autoReadEnabled = signal(localStorage.getItem('chat_autoRead') === 'true');
const chatSoundsEnabled = signal(localStorage.getItem('chat_sounds') === 'true');
const pushState = signal<'unknown' | 'prompt' | 'subscribing' | 'granted' | 'denied' | 'unsupported'>('unknown');
const pushDismissed = signal(localStorage.getItem('push_prompt_dismissed') === 'true');

// Sons de chat via hook centralizado
const { playSendSound, playReceiveSound } = useChatSounds(chatSoundsEnabled);
const isListening = signal(false);
const isJarvisMode = signal(false);
const isSpeaking = signal(false);
const voiceTranscript = signal('');
let jarvisIdleTimer: any = null;
let lastSpeechTime = 0;
const JARVIS_IDLE_TIMEOUT = 60000;

const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
let recognition: any = null;
let speechSynth = window.speechSynthesis;

function speakText(text: string): Promise<void> {
  return new Promise((resolve) => {
    const clean = text
      .replace(/```[\s\S]*?```/g, ' código omitido ')
      .replace(/`[^`]+`/g, (m) => m.slice(1, -1))
      .replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\*([^*]+)\*/g, '$1')
      .replace(/#{1,6}\s/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      .replace(/[_~]/g, '').replace(/<[^>]+>/g, '').replace(/\n+/g, '. ').trim();
    if (!clean) { resolve(); return; }
    speechSynth.cancel();
    const utterance = new SpeechSynthesisUtterance(clean);
    utterance.lang = 'pt-BR'; utterance.rate = 1.1;
    const voices = speechSynth.getVoices();
    const ptVoice = voices.find((v: any) => v.lang.startsWith('pt')) || voices[0];
    if (ptVoice) utterance.voice = ptVoice;
    isSpeaking.value = true;
    utterance.onend = () => { isSpeaking.value = false; resolve(); };
    utterance.onerror = () => { isSpeaking.value = false; resolve(); };
    speechSynth.speak(utterance);
  });
}

function stopSpeaking() { speechSynth.cancel(); isSpeaking.value = false; }

function resetJarvisIdleTimer() {
  if (jarvisIdleTimer) clearTimeout(jarvisIdleTimer);
  jarvisIdleTimer = setTimeout(() => {
    if (isJarvisMode.value && (Date.now() - lastSpeechTime) >= JARVIS_IDLE_TIMEOUT) stopListening();
  }, JARVIS_IDLE_TIMEOUT);
}

function startListening(jarvis = false) {
  if (!SpeechRecognition) { alert(i18next.t('chat:voice_not_supported')); return; }
  if (recognition) { recognition.abort(); recognition = null; }
  recognition = new SpeechRecognition();
  recognition.lang = 'pt-BR'; recognition.interimResults = true; recognition.continuous = jarvis;
  recognition.onstart = () => { isListening.value = true; voiceTranscript.value = ''; };
  recognition.onresult = (event: any) => {
    let interim = '', final = '';
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const t = event.results[i][0].transcript;
      if (event.results[i].isFinal) final += t; else interim += t;
    }
    voiceTranscript.value = final || interim;
    lastSpeechTime = Date.now(); resetJarvisIdleTimer();
    if (final && jarvis) {
      voiceTranscript.value = '';
      if (final.trim()) {
        inputText.value = final.trim();
        sendMessage().then(() => {
          const check = setInterval(() => {
            const last = messages.value[messages.value.length - 1];
            if (last?.role === 'assistant' && !last.id.startsWith('opt_')) {
              clearInterval(check);
              speakText(last.content).then(() => {
                if (isJarvisMode.value && recognition) try { recognition.start(); } catch {}
              });
            }
          }, 2000);
          setTimeout(() => clearInterval(check), 60000);
        });
      }
    }
  };
  recognition.onend = () => {
    if (!jarvis) {
      const t = voiceTranscript.value.trim();
      isListening.value = false; voiceTranscript.value = '';
      if (t) { inputText.value = t; sendMessage(); }
    } else if (isJarvisMode.value && !isSpeaking.value) {
      setTimeout(() => { if (isJarvisMode.value && recognition) try { recognition.start(); } catch {} }, 300);
    } else { isListening.value = false; voiceTranscript.value = ''; }
  };
  recognition.onerror = (e: any) => {
    if (e.error === 'not-allowed') { alert(i18next.t('chat:mic_denied')); isListening.value = false; isJarvisMode.value = false; return; }
    if (jarvis && isJarvisMode.value && (e.error === 'no-speech' || e.error === 'aborted')) return;
    isListening.value = false; isJarvisMode.value = false; voiceTranscript.value = '';
  };
  if (jarvis) { isJarvisMode.value = true; lastSpeechTime = Date.now(); resetJarvisIdleTimer(); }
  recognition.start();
}

function stopListening() {
  isJarvisMode.value = false; isListening.value = false; voiceTranscript.value = '';
  if (jarvisIdleTimer) { clearTimeout(jarvisIdleTimer); jarvisIdleTimer = null; }
  stopSpeaking();
  if (recognition) { recognition.abort(); recognition = null; }
}

const POLL_INTERVAL = 3000;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

// SVG icons in ID Visual V2 style — neutral/light colors on dark bg
const IC = {
  mac: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="${T.textSecondary}"/></svg>`,
  linux: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.536.058.399.116.728.04.97-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.368 1.884 1.43.868.07 1.726-.24 2.456-.79.366-.268.664-.602.88-1.004.167-.31.22-.6.257-.867.014-.168.029-.336.044-.504.045-.396.089-.732-.051-1.2a2.03 2.03 0 00-.11-.272 1.6 1.6 0 00-.023-.045c-.141-.27-.325-.455-.483-.667-.16-.213-.353-.467-.329-.722 0-.197.163-.399.346-.599.19-.202.42-.398.554-.658a1.22 1.22 0 00.141-.612c-.004-.29-.116-.475-.226-.634-.055-.08-.102-.153-.155-.236a5.308 5.308 0 00-.153-.276 3.29 3.29 0 01-.072-.116c-.123-.21-.209-.464-.209-.794 0-.565.226-1.398.385-2.13.162-.728.32-1.363.32-1.793 0-.748-.356-1.337-.754-1.758a3.89 3.89 0 00-.373-.335c.08-.405.12-.79.1-1.143-.08-1.49-.96-5.965-5.24-6.298A5.57 5.57 0 0012.504 0z" fill="${T.textSecondary}"/></svg>`,
  win: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" fill="${T.textSecondary}"/></svg>`,
  pc: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="3" width="20" height="14" rx="2" stroke="${T.textSecondary}" stroke-width="1.5" fill="none"/><path d="M8 21h8M12 17v4" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  chat: `<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="${T.textSecondary}" stroke-width="1.5" fill="none"/></svg>`,
  mic: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" stroke="${T.textSecondary}" stroke-width="1.5"/><path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  jarvis: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="${T.textSecondary}" stroke-width="1.5"/><path d="M12 16v.01M8 12a4 4 0 018 0" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  attach: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  send: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
  img: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" stroke="${T.textSecondary}" stroke-width="1.5"/><circle cx="8.5" cy="8.5" r="1.5" fill="${T.textSecondary}"/><path d="M21 15l-5-5L5 21" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  doc: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><polyline points="14 2 14 8 20 8" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><line x1="16" y1="13" x2="8" y2="13" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/><line x1="16" y1="17" x2="8" y2="17" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  speaker: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H2v6h4l5 4V5zM19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  mute: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M11 5L6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  wrench: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="${T.textSecondary}" stroke-width="1.5" stroke-linecap="round"/></svg>`,
  bot: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="3" y="8" width="18" height="12" rx="2" stroke={T.purple} stroke-width="1.5"/><circle cx="9" cy="14" r="1.5" fill={T.purple}/><circle cx="15" cy="14" r="1.5" fill={T.purple}/><path d="M12 2v4M8 6h8" stroke={T.purple} stroke-width="1.5" stroke-linecap="round"/></svg>`,
  openclaw: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill={T.purple}/></svg>`,
};

function getOsIcon(os: string): string {
  const l = (os || '').toLowerCase();
  if (l.includes('darwin') || l.includes('mac')) return IC.mac;
  if (l.includes('linux')) return IC.linux;
  if (l.includes('win')) return IC.win;
  return IC.pc;
}

function getOsLabel(os: string): string {
  const l = (os || '').toLowerCase();
  if (l.includes('darwin') || l.includes('mac')) return 'macOS';
  if (l.includes('linux')) return 'Linux';
  if (l.includes('win')) return 'Windows';
  return os || '?';
}

function formatTime(ts?: string): string {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(ts?: string): string {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return i18next.t('chat:today');
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return i18next.t('chat:yesterday_label');
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function formatTimeAgo(ts: string): string {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

// Check if message is internal system noise (should be hidden from end-user)
function isSystemNoise(text: string): boolean {
  if (!text) return false;
  const t = text.trim();
  // OpenClaw heartbeat responses
  if (t === 'HEARTBEAT_OK' || t === 'NO_REPLY') return true;
  // System exec notifications
  if (/^System: \[.*\] Exec completed/.test(t)) return true;
  // Heartbeat prompts
  if (t.includes('Read HEARTBEAT.md if it exists') && t.includes('reply HEARTBEAT_OK')) return true;
  // Pure metadata-only messages (no real user content)
  if (/^(Conversation info \(untrusted metadata\)|Pre-compaction memory flush)/.test(t)) return true;
  // System event notifications with file paths only
  if (/^System: \[.*\].*\n.*node_modules\//.test(t)) return true;
  return false;
}

// toolMeta importado de constants/tools.ts

const friendlySummary = (td: any) => {
  if (!td) return '';
  const s = td.summary || '';
  if (s.startsWith('exec: ')) return s.substring(6).replace(/\/data\/\.openclaw\/workspace\//g, '~/');
  if (s.startsWith('edit: ') || s.startsWith('read: ') || s.startsWith('write: '))
    return s.split(': ')[1]?.replace(/\/data\/\.openclaw\/workspace\//g, '~/') || s;
  if (s.startsWith('search: ')) return `"${s.substring(8)}"`;
  if (s.startsWith('fetch: ')) return s.substring(7);
  if (s.startsWith('memory_search: ')) return `"${s.substring(15)}"`;
  return s;
};

// ─── API ─────────────────────────────────────────────────────────────────────

async function loadMachines() {
  try {
    const res = await api.get('/runner-v3/list') as any;
    const runners = res?.data?.runners || res?.runners || [];
    const list: ChatMachine[] = runners.map((r: any) => {
      const isOnline = r.status === 'online' && r.last_seen_at &&
        (Date.now() - new Date(r.last_seen_at).getTime() < 180_000);
      return {
        id: r.id, name: r.name || i18next.t('chat:no_name'),
        agentName: r.agent_name || r.name || i18next.t('chat:agent'),
        agentModel: r.agent_model || undefined,
        status: isOnline ? 'online' : 'offline' as const,
        os: r.os || '', lastSeen: r.last_seen_at || '',
        hasOpenclaw: !!r.has_openclaw,
      };
    });
    list.sort((a, b) => {
      if (a.status !== b.status) return a.status === 'online' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    machinesList.value = list;

    // Auto-fix stale runner: if selected runner no longer exists (reinstalled), switch to new one with same name
    const sel = selectedMachine.value;
    if (sel && !list.find(m => m.id === sel.id)) {
      const replacement = list.find(m => m.name === sel.name) || list.find(m => m.status === 'online');
      if (replacement) {
        selectedMachine.value = replacement;
        localStorage.setItem('chat_selectedRunner', replacement.id);
        console.log(`[Chat] Runner ${sel.id.slice(-8)} replaced by ${replacement.id.slice(-8)} (same name: ${replacement.name})`);
      }
    }
  } catch {} finally { machinesLoading.value = false; }
}

async function loadMessages() {
  const m = selectedMachine.value;
  if (!m) { loading.value = false; return; }
  try {
    const data = await api.get<any>(`/runner-v3/${m.id}/chat/messages?limit=500`);
    const rawMsgs = data?.data?.messages || data?.messages || [];
    // Expand toolData into separate tool_call messages (shown when "Ações" is on)
    const serverMsgs: ChatMessage[] = [];
    for (const msg of rawMsgs) {
      // Insert tool_call entries BEFORE the assistant message that used them
      if (msg.role === 'assistant' && msg.toolData && msg.toolData.length > 0) {
        for (const tc of msg.toolData) {
          serverMsgs.push({
            id: `tc_${msg.id}_${tc.id || tc.name}`,
            role: 'tool_call',
            content: JSON.stringify({ name: tc.name, summary: tc.summary || tc.name }),
            timestamp: msg.timestamp,
          });
        }
      }
      // Skip assistant messages with empty content (tool-only, already expanded above)
      if (msg.role === 'assistant' && !msg.content?.trim()) continue;
      serverMsgs.push(msg);
    }
    const newLast = serverMsgs.length > 0 ? serverMsgs[serverMsgs.length - 1]?.timestamp || '' : '';
    if (newLast !== lastMessageTs.value || serverMsgs.length !== messages.value.filter(m => !m.id.startsWith('opt_')).length) {
      const optimistic = messages.value.filter(m => m.id.startsWith('opt_'));
      const confirmedIds = new Set<string>();
      for (const opt of optimistic) {
        const match = serverMsgs.find((s: any) =>
          s.role === 'user' && s.content === opt.content &&
          Math.abs(new Date(s.timestamp).getTime() - new Date(opt.timestamp).getTime()) < 120000
        );
        if (match) confirmedIds.add(opt.id);
      }
      const stillPending = optimistic.filter(o => !confirmedIds.has(o.id));
      messages.value = [...serverMsgs, ...stillPending];
      lastMessageTs.value = newLast;
    }
    api.get<any>(`/runner-v3/${m.id}/chat/activity`).then(d => {
      const act = d?.data || d;
      if (act?.status) activityState.value = act;
    }).catch(() => {});
  } catch {} finally { loading.value = false; }
}

async function addImages(files: FileList | File[]) {
  for (const file of Array.from(files)) {
    if (!ACCEPTED_TYPES.includes(file.type) || file.size > MAX_IMAGE_SIZE) continue;
    if (pendingImages.value.length >= 4) break;
    const dataUrl = await fileToDataUrl(file);
    pendingImages.value = [...pendingImages.value, { file, dataUrl }];
  }
}

async function subscribeToPushIfNeeded() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();
    if (existing) return; // already subscribed
    const vapidRes = await api.get('/notifications/vapid-key').catch(() => null) as any;
    const vapidKey = vapidRes?.data?.publicKey || vapidRes?.publicKey;
    if (!vapidKey) return;
    const padding = '='.repeat((4 - (vapidKey.length % 4)) % 4);
    const b64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(b64);
    const arr = new Uint8Array(Array.from(raw).map(c => c.charCodeAt(0)));
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: arr });
    const json = sub.toJSON();
    await api.post('/notifications/subscribe', { endpoint: json.endpoint, keys: json.keys }).catch(() => {});
  } catch (err) { console.error('[Push] Subscribe error:', err); }
}

async function activatePush() {
  pushState.value = 'subscribing';
  try {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      await subscribeToPushIfNeeded();
      pushState.value = 'granted';
    } else {
      pushState.value = 'denied';
    }
  } catch { pushState.value = 'denied'; }
}

function dismissPushPrompt() {
  pushState.value = 'unknown';
  pushDismissed.value = true;
  localStorage.setItem('push_prompt_dismissed', 'true');
}

async function sendMessage() {
  const m = selectedMachine.value;
  if (!m) return;
  const text = inputText.value.trim();
  const images = pendingImages.value;
  if ((!text && images.length === 0) || sending.value) return;
  inputText.value = '';
  pendingImages.value = [];
  // Reset textarea height after clearing text
  const ta = document.querySelector('[data-chat-input]') as HTMLTextAreaElement;
  if (ta) ta.style.height = 'auto';
  sending.value = true;
  playSendSound();
  const optId = `opt_${Date.now()}`;
  messages.value = [...messages.value, {
    id: optId, role: 'user',
 content: text || i18next.t('chat:image'),
    timestamp: new Date().toISOString(),
    attachments: images.map(img => ({ type: 'image', mimeType: img.file.type, dataUrl: img.dataUrl })),
  }];
  pendingSendIds.value = new Set([...pendingSendIds.value, optId]);
  try {
    const attachments = images.map(img => ({ type: 'image' as const, mimeType: img.file.type, dataUrl: img.dataUrl }));
    await api.post<any>(`/runner-v3/${m.id}/chat/send`, {
      message: text || '(imagem)',
      attachments: attachments.length > 0 ? attachments : undefined,
    });
  } catch (err) { console.error('Send failed:', err); }
  finally { sending.value = false; }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ChatPage() {
  const { t } = useTranslation('chat');
  // if (!isAdmin.value && !hasActivePlan.value) return <NoPlanGate />;
  const endRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<any>(null);
  const machineIntervalRef = useRef<any>(null);
  const prevLen = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const attachBtnRef = useRef<HTMLButtonElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isNearBottom = useRef(true);

  // Override main-content styles: remove padding + overflow so Chat controls its own layout
  useEffect(() => {
    const main = document.querySelector('.main-content') as HTMLElement;
    if (!main) return;
    const saved: Record<string, string> = {};
    const props = ['padding', 'overflow', 'overflowY', 'display', 'flexDirection'] as const;
    props.forEach(p => { saved[p] = (main.style as any)[p]; });
    main.classList.add('chat-active');
    main.style.padding = '0';
    main.style.overflow = 'hidden';
    main.style.overflowY = 'hidden';
    main.style.display = 'flex';
    main.style.flexDirection = 'column';
    // Prevent any scroll on main-content (iOS Safari workaround)
    const preventScroll = () => { main.scrollTop = 0; };
    const preventTouchMove = (e: TouchEvent) => {
      // Only prevent if the touch target is NOT inside the chat messages area
      const chatArea = document.querySelector('[data-chat-messages]');
      if (chatArea && chatArea.contains(e.target as Node)) return;
      e.preventDefault();
    };
    main.addEventListener('scroll', preventScroll, { passive: false });
    main.addEventListener('touchmove', preventTouchMove, { passive: false });

    // Hide bottom nav when keyboard opens (detected via focus on textarea)
    const bottomNav = document.querySelector('.bottom-nav') as HTMLElement;
    let keyboardOpen = false;
    const onFocusIn = (e: FocusEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'TEXTAREA' || tag === 'INPUT') {
        keyboardOpen = true;
        if (bottomNav) bottomNav.style.display = 'none';
        // Resize container to visible viewport height (above keyboard)
        const applyKBHeight = () => {
          if (!window.visualViewport || !keyboardOpen) return;
          main.style.height = `${window.visualViewport.height}px`;
          main.style.maxHeight = `${window.visualViewport.height}px`;
          // Prevent Safari from scrolling the page
          window.scrollTo(0, 0);
        };
        setTimeout(applyKBHeight, 100);
        setTimeout(applyKBHeight, 300);
        setTimeout(applyKBHeight, 500);
      }
    };
    const onFocusOut = () => {
      keyboardOpen = false;
      if (bottomNav) bottomNav.style.display = '';
      main.style.height = '';
      main.style.maxHeight = '';
      setTimeout(() => { window.scrollTo(0, 0); main.scrollTop = 0; }, 100);
    };
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    // Also listen to visualViewport for real-time resize during keyboard animation
    const onVVResize = () => {
      if (!keyboardOpen || !window.visualViewport) return;
      main.style.height = `${window.visualViewport.height}px`;
      main.style.maxHeight = `${window.visualViewport.height}px`;
      window.scrollTo(0, 0);
    };
    window.visualViewport?.addEventListener('resize', onVVResize);

    return () => {
      main.removeEventListener('scroll', preventScroll);
      main.removeEventListener('touchmove', preventTouchMove);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      window.visualViewport?.removeEventListener('resize', onVVResize);
      if (bottomNav) bottomNav.style.display = '';
      main.classList.remove('chat-active');
      props.forEach(p => { (main.style as any)[p] = saved[p]; });
      main.style.height = '';
      main.style.maxHeight = '';
    };
  }, []);

  const checkScroll = useCallback(() => {
    const el = chatAreaRef.current;
    if (!el) return;
    const near = (el.scrollHeight - el.scrollTop - el.clientHeight) < 150;
    isNearBottom.current = near;
    setShowScrollBtn(!near);
    if (near) { setUnreadCount(0); }
  }, []);

  const handleSelectMachine = useCallback((machine: ChatMachine) => {
    selectedMachine.value = machine;
    machineDropdownOpen.value = false;
    messages.value = [];
    lastMessageTs.value = '';
    loading.value = true;
    prevLen.current = 0;
    localStorage.setItem('chat_selectedRunner', machine.id);
    loadMessages().then(() => {
      // Force scroll to bottom after messages load (mobile needs extra delay for DOM render)
      setTimeout(() => scrollToBottom(true), 100);
      setTimeout(() => scrollToBottom(true), 300);
    });
  }, []);

  // Load machines + auto-select saved
  useEffect(() => {
    loadMachines().then(() => {
      const savedId = localStorage.getItem('chat_selectedRunner');
      if (savedId && !selectedMachine.value) {
        const found = machinesList.value.find(m => m.id === savedId);
        if (found) handleSelectMachine(found);
      }
    });
    machineIntervalRef.current = setInterval(loadMachines, 15000);

    // Presence heartbeat — every 30s
    const heartbeatInterval = setInterval(() => {
      const m = selectedMachine.value;
      if (m && !document.hidden) {
        api.post(`/runner-v3/${m.id}/chat/heartbeat`, {}).catch(() => {});
      }
    }, 30000);

    // Push notification — check state and show prompt if needed
    (async () => {
      try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
          pushState.value = 'unsupported'; return;
        }
        if (Notification.permission === 'denied') { pushState.value = 'denied'; return; }
        if (Notification.permission === 'granted') {
          // Already granted — ensure subscribed
          await subscribeToPushIfNeeded();
          pushState.value = 'granted'; return;
        }
        // Permission is 'default' — show friendly prompt (unless dismissed)
        if (!pushDismissed.value) pushState.value = 'prompt';
      } catch { pushState.value = 'unsupported'; }
    })();

    // Visibility change — mark offline when hidden
    const handleVisibility = () => {
      const m = selectedMachine.value;
      if (m && !document.hidden) {
        api.post(`/runner-v3/${m.id}/chat/heartbeat`, {}).catch(() => {});
        api.post(`/runner-v3/${m.id}/chat/mark-read`, {}).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (machineIntervalRef.current) clearInterval(machineIntervalRef.current);
      clearInterval(heartbeatInterval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        machineDropdownOpen.value = false;
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Auto-scroll + new messages
  useEffect(() => {
    const newCount = messages.value.length - prevLen.current;
    if (messages.value.length !== prevLen.current) {
      if (prevLen.current === 0) {
        setTimeout(() => scrollToBottom(true), 50);
      } else if (isNearBottom.current) {
        setTimeout(() => scrollToBottom(), 50);
      } else if (newCount > 0) {
        setUnreadCount(prev => prev + newCount);
      }
    }
    // Sound notification for new messages
    if (newCount > 0 && prevLen.current > 0) {
      const newMsgs = messages.value.slice(-newCount);
      const lastAssistant = [...newMsgs].reverse().find(m => m.role === 'assistant' && !m.id.startsWith('opt_'));
      if (lastAssistant) {
        playReceiveSound();
        if (isJarvisMode.value) speakText(lastAssistant.content);
      }
    }
    prevLen.current = messages.value.length;
    // Mark as read if chat is visible
    const m = selectedMachine.value;
    if (m && !document.hidden) {
      api.post(`/runner-v3/${m.id}/chat/mark-read`, {}).catch(() => {});
      setUnreadCount(0);
    }
  }, [messages.value.length]);

  const scrollToBottom = useCallback((instant?: boolean) => {
    const el = chatAreaRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'instant' as any : 'smooth' });
    setShowScrollBtn(false); setUnreadCount(0);
  }, []);

  // Poll messages
  useEffect(() => {
    if (selectedMachine.value) {
      loadMessages();
      intervalRef.current = setInterval(loadMessages, POLL_INTERVAL);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [selectedMachine.value?.id]);

  // Poll deploy status — only when a machine is selected, uses that machine's GitHub integration
  useEffect(() => {
    const mid = selectedMachine.value?.id;
    if (!mid) { deployStatus.value = null; return; }
    const pollDeploys = async () => {
      try {
        const res = await api.get<any>(`/integrations/github/deploys?machineId=${mid}`);
        const runs = res?.deploys || [];
        if (runs.length === 0) { deployStatus.value = null; return; }
        const latest = runs[0];
        const isActive = latest.status === 'in_progress' || latest.status === 'queued';
        const isRecent = latest.updatedAt && (Date.now() - new Date(latest.updatedAt).getTime() < 300000);
        if (isActive || isRecent) {
          deployStatus.value = { status: latest.conclusion || latest.status, sha: latest.sha || '', message: latest.message || '', createdAt: latest.createdAt, url: latest.url };
        } else {
          deployStatus.value = null;
        }
      } catch { deployStatus.value = null; }
    };
    pollDeploys();
    const iv = setInterval(pollDeploys, 10000);
    return () => { clearInterval(iv); deployStatus.value = null; };
  }, [selectedMachine.value?.id]);

  // Poll activity (fast during streaming for real-time text)
  const activityIntervalRef = useRef<any>(null);
  useEffect(() => {
    const m = selectedMachine.value;
    if (!m) return;
    const pollActivity = () => {
      api.get<any>(`/runner-v3/${m.id}/chat/activity`).then(d => {
        const act = d?.data || d;
        if (act?.status) {
          const wasStreaming = activityState.value.status === 'streaming';
          activityState.value = act;
          // Auto-scroll during streaming
          if (act.status === 'streaming' && isNearBottom.current) {
            setTimeout(() => scrollToBottom(), 50);
          }
          // When streaming ends, reload messages to get final version
          if (wasStreaming && act.status !== 'streaming') {
            loadMessages();
          }
        }
      }).catch(() => {});
    };
    // Poll faster during streaming (800ms), normal otherwise (3s)
    const scheduleNext = () => {
      const isActive = activityState.value.status === 'streaming' || activityState.value.status === 'tool';
      const interval = isActive ? 800 : 3000;
      activityIntervalRef.current = setTimeout(() => { pollActivity(); scheduleNext(); }, interval);
    };
    pollActivity();
    scheduleNext();
    return () => { if (activityIntervalRef.current) clearTimeout(activityIntervalRef.current); };
  }, [selectedMachine.value?.id]);

  // Paste handler
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imgs: File[] = [];
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) { const f = item.getAsFile(); if (f) imgs.push(f); }
      }
      if (imgs.length > 0) { e.preventDefault(); addImages(imgs); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  const dragCounter = useRef(0);

  // Close drop modal / attach menu on Escape + outside click
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dropModalImages.value.length > 0) { dropModalImages.value = []; dropModalText.value = ''; }
        if (dropModalDocs.value.length > 0) { dropModalDocs.value = []; dropModalDocText.value = ''; }
        if (attachMenuOpen.value) attachMenuOpen.value = false;
      }
    };
    const onClick = (e: MouseEvent) => {
      if (attachMenuOpen.value && attachBtnRef.current && !attachBtnRef.current.contains(e.target as Node)) {
        attachMenuOpen.value = false;
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, []);

  const machine = selectedMachine.value;
  const onlineMachines = machinesList.value.filter(m => m.status === 'online');
  const offlineMachines = machinesList.value.filter(m => m.status === 'offline');
  let lastDate = '';

  const handleRootDragEnter = useCallback((e: DragEvent) => { e.preventDefault(); dragCounter.current++; dragOver.value = true; }, []);
  const handleRootDragOver = useCallback((e: DragEvent) => { e.preventDefault(); }, []);
  const handleRootDragLeave = useCallback((e: DragEvent) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current <= 0) { dragCounter.current = 0; dragOver.value = false; } }, []);
  const handleRootDrop = useCallback(async (e: DragEvent) => {
    e.preventDefault(); dragCounter.current = 0; dragOver.value = false;
    if (!e.dataTransfer?.files?.length) return;
    const imgs: Array<{ file: File; dataUrl: string }> = [];
    const docs: Array<{ file: File; dataUrl: string }> = [];
    for (const file of Array.from(e.dataTransfer.files)) {
      if (ACCEPTED_TYPES.includes(file.type) && file.size <= MAX_IMAGE_SIZE) {
        if (imgs.length < 4) { const dataUrl = await fileToDataUrl(file); imgs.push({ file, dataUrl }); }
      } else if (file.size <= MAX_DOC_SIZE && DOC_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))) {
        if (docs.length < 4) { const dataUrl = await fileToDataUrl(file); docs.push({ file, dataUrl }); }
      }
    }
    if (imgs.length > 0) { dropModalImages.value = imgs; dropModalText.value = ''; }
    else if (docs.length > 0) { dropModalDocs.value = docs; dropModalDocText.value = ''; }
  }, []);

  return (
    <div
      onDragEnter={handleRootDragEnter} onDragOver={handleRootDragOver}
      onDragLeave={handleRootDragLeave} onDrop={handleRootDrop}
      style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', maxHeight: '100%', width: '100%',
      overflow: 'hidden',
      background: T.bg,
    }}>
      {/* ═══ TOP BAR: Machine Selector ═══ */}
      <div style={{
        padding: '10px 12px',
        borderBottom: `1px solid ${T.border}`,
        background: T.bgCard,
        display: 'flex', alignItems: 'center', gap: 10,
        flexShrink: 0, zIndex: 100,
      }}>
        {/* Machine dropdown trigger */}
        <div ref={dropdownRef} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Button variant="ghost"
            onClick={() => { machineDropdownOpen.value = !machineDropdownOpen.value; }}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, width: '100%',
              padding: '8px 12px', borderRadius: 12,
              background: machine ? T.primaryBg : T.bgCard,
              border: `1px solid ${machine ? T.primaryBg : T.bgHover}`,
              cursor: 'pointer', textAlign: 'left', color: T.text,
              transition: 'all 0.15s',
            }}
          >
            {machine ? (
              <>
                {/* OS icon + status dot */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <span style={{ display: "inline-flex" }} dangerouslySetInnerHTML={{ __html: getOsIcon(machine.os) }} />
                  <OnlineDot online={machine.status === 'online'} style={{
                    position: 'absolute', bottom: -2, right: -2,
                    border: `2px solid ${T.bgCard}`,
                    boxShadow: machine.status === 'online' ? `0 0 6px ${T.successBgStrong}` : 'none',
                  }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {activityState.value.agentName || machine.agentName || machine.name}
                  </div>
                  <div style={{ fontSize: 11, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <span>{machine.name}</span>
                    {machine.hasOpenclaw && (
                      <>
                        <span style={{ opacity: 0.4 }}> · </span>
                        <span style={{ color: T.purple, fontSize: 10 }}>Agente IA</span>
                      </>
                    )}
                    {machine.agentModel && (
                      <>
                        <span style={{ opacity: 0.4 }}> · </span>
                        <span style={{ color: T.accent, fontSize: 10 }}>
                          {machine.agentModel.includes('opus') ? 'Opus' :
                           machine.agentModel.includes('sonnet') ? 'Sonnet' :
                           machine.agentModel.includes('haiku') ? 'Haiku' :
                           machine.agentModel.includes('gpt-4') ? 'GPT-4' :
                           machine.agentModel.includes('gpt-3') ? 'GPT-3.5' :
                           machine.agentModel.split('/').pop()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <span style={{ display: 'inline-flex' }} dangerouslySetInnerHTML={{ __html: IC.pc }} />
                <span style={{ fontSize: 14, color: T.textMuted }}>Selecione uma máquina...</span>
              </div>
            )}
            <span style={{ fontSize: 12, color: T.textMuted, flexShrink: 0, transition: 'transform 0.2s', transform: machineDropdownOpen.value ? 'rotate(180deg)' : 'none' }}>▼</span>
          </Button>

          {/* Dropdown */}
          {machineDropdownOpen.value && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
              background: T.bgCard, border: `1px solid ${T.border}`,
              borderRadius: 14, boxShadow: `0 12px 40px ${T.bgOverlay}`,
              maxHeight: 400, overflowY: 'auto', zIndex: 200,
              animation: 'dropdownIn 0.15s ease-out',
            }}>
              {machinesLoading.value ? (
                <div style={{ padding: 20, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>
                  <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
                </div>
              ) : machinesList.value.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>
                  <div style={{ marginBottom: 8 }} dangerouslySetInnerHTML={{ __html: IC.pc.replace(/20/g, '28') }} />
                  <div style={{ fontSize: 13, fontWeight: 500 }}>Nenhuma máquina conectada</div>
                  <div style={{ fontSize: 11, marginTop: 4, opacity: 0.6 }}>Instale o runner para começar</div>
                </div>
              ) : (
                <>
                  {/* Online machines */}
                  {onlineMachines.length > 0 && (
                    <>
                      <div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, color: T.success, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        ● Online ({onlineMachines.length})
                      </div>
                      {onlineMachines.map(m => (
                        <MachineItem key={m.id} machine={m} selected={machine?.id === m.id} onSelect={handleSelectMachine} />
                      ))}
                    </>
                  )}
                  {/* Offline machines */}
                  {offlineMachines.length > 0 && (
                    <>
                      <div style={{ padding: '10px 14px 4px', fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        ○ Offline ({offlineMachines.length})
                      </div>
                      {offlineMachines.map(m => (
                        <MachineItem key={m.id} machine={m} selected={machine?.id === m.id} onSelect={handleSelectMachine} />
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Action toggles */}
        <Button variant="ghost"
          className="chat-action-btn"
          onClick={() => { showActions.value = !showActions.value; localStorage.setItem('chat_showActions', String(showActions.value)); }}
          title={showActions.value ? t('showing_actions') : t('only_messages')}
          style={{
            padding: '6px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600,
            background: showActions.value ? T.purpleBg : T.bgCard,
            border: `1px solid ${showActions.value ? T.purpleBg : T.bgHover}`,
            color: showActions.value ? T.purple : T.textMuted,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0,
            transition: 'all 0.2s',
          }}
        >
          <span dangerouslySetInnerHTML={{ __html: showActions.value ? IC.wrench : IC.chat }} /> {showActions.value ? t('actions') : t('messages_short')}
        </Button>

        {/* Sound notifications toggle */}
        <Button variant="ghost"
          className="chat-action-btn"
          onClick={() => {
            chatSoundsEnabled.value = !chatSoundsEnabled.value;
            localStorage.setItem('chat_sounds', String(chatSoundsEnabled.value));
            if (chatSoundsEnabled.value) playSendSound(); // Preview sound when enabling
          }}
          title={chatSoundsEnabled.value ? t('sounds_on') : t('sounds_off')}
          style={{
            padding: '6px 10px', borderRadius: 20, fontSize: 13,
            background: chatSoundsEnabled.value ? T.primaryBg : T.bgCard,
            border: `1px solid ${chatSoundsEnabled.value ? T.primaryBg : T.bgHover}`,
            color: chatSoundsEnabled.value ? T.primary : T.textMuted,
            cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s',
          }}
         dangerouslySetInnerHTML={{ __html: chatSoundsEnabled.value ? IC.speaker : IC.mute }} />

          {/* Push notification toggle */}
          <Button variant="ghost"
            className="chat-action-btn"
            onClick={() => {
              if (pushState.value === 'granted') {
                // Already active — nothing to toggle for now
              } else if (pushState.value === 'denied') {
                alert(t('notifications_blocked'));
              } else {
                activatePush();
              }
            }}
            title={pushState.value === 'granted' ? t('notifications_active') : pushState.value === 'denied' ? t('notifications_blocked_title') : t('enable_notifications')}
            style={{
              padding: '6px 10px', borderRadius: 20, fontSize: 13,
              background: pushState.value === 'granted' ? T.successBg : T.bgCard,
              border: `1px solid ${pushState.value === 'granted' ? T.successBg : T.bgHover}`,
              color: pushState.value === 'granted' ? T.success : pushState.value === 'denied' ? T.danger : T.textMuted,
              cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
              <path d="M13.73 21a2 2 0 01-3.46 0"/>
              {pushState.value === 'denied' && <line x1="1" y1="1" x2="23" y2="23" stroke={T.danger} strokeWidth="2"/>}
            </svg>
          </Button>
      </div>

      {/* ═══ DEPLOY STATUS BAR ═══ */}
      {deployStatus.value && (() => {
        const ds = deployStatus.value;
        const isActive = ds.status === 'in_progress' || ds.status === 'queued';
        const isSuccess = ds.status === 'success';
        const isFail = ds.status === 'failure';
        const color = isActive ? T.warning : isSuccess ? T.success : isFail ? T.danger : T.textSecondary;
 const label = isActive ? (ds.status === 'queued' ? t('deploy_queued') : t('deploy_uploading')) : isSuccess ? t('deploy_done') : isFail ? t('deploy_failed') : ds.status;
        return (
          <div style={{
            padding: '8px 14px', flexShrink: 0,
            background: `${color}08`, borderBottom: `1px solid ${color}20`,
            display: 'flex', alignItems: 'center', gap: 10,
            cursor: ds.url ? 'pointer' : 'default',
          }}
            onClick={() => { if (ds.url) window.open(ds.url, '_blank'); }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0,
              ...(isActive ? { animation: 'pulse 1.5s ease-in-out infinite' } : {}),
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color, display: 'flex', alignItems: 'center', gap: 6 }}>
                {label}
                {ds.sha && <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: T.primary, background: T.primaryBg, padding: '1px 5px', borderRadius: 3 }}>{ds.sha}</span>}
              </div>
              {ds.message && <div style={{ fontSize: 11, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 1 }}>{ds.message}</div>}
            </div>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"/></svg>
          </div>
        );
      })()}

      {/* ═══ MAIN CHAT AREA ═══ */}
      {!machine ? (
        /* No machine selected — empty state */
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 20, padding: 40,
        }}>
          <div style={{
            width: 100, height: 100, borderRadius: 28,
            background: `linear-gradient(135deg, ${T.primaryBg}, ${T.accentBg})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${T.primaryBg}`,
          }}>
            <span dangerouslySetInnerHTML={{ __html: IC.chat.replace(/width="20"/,"width=\"64\"").replace(/height="20"/,"height=\"64\"") }} />
          </div>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: T.text }}>
              Converse com seu agente
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: T.textMuted, maxWidth: 340, lineHeight: 1.6 }}>
              Selecione uma máquina no topo para começar a conversar com o agente de IA instalado nela.
            </p>
          </div>
          <Button
            variant="primary"
            size="lg"
            onClick={() => { machineDropdownOpen.value = true; }}
            style={{
              borderRadius: 14,
              background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
              boxShadow: `0 4px 20px ${T.primaryBg}`,
            }}
          >
            Selecionar Máquina
          </Button>

          {/* Quick machine pills for online machines */}
          {onlineMachines.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8 }}>
              {onlineMachines.map(m => (
                <Button variant="ghost"
                  key={m.id}
                  onClick={() => handleSelectMachine(m)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 20,
                    background: T.successBg, border: `1px solid ${T.successBg}`,
                    color: T.text, fontSize: 12, cursor: 'pointer',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.success }} />
                  <span style={{ display: "inline-flex" }} dangerouslySetInnerHTML={{ __html: getOsIcon(m.os) }} />
                  <span style={{ fontWeight: 600 }}>{m.name}</span>
 {m.hasOpenclaw && <span style={{ fontSize: 10, color: T.purple }}></span>}
                </Button>
              ))}
            </div>
          )}
        </div>
      ) : !machine.hasOpenclaw ? (
        /* Machine without OpenClaw */
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 20, padding: 40,
        }}>
          <div style={{
            width: 90, height: 90, borderRadius: 24,
            background: `linear-gradient(135deg, ${T.warningBg}, ${T.dangerBg})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${T.warningBg}`,
          }}>
            <span dangerouslySetInnerHTML={{ __html: IC.bot.replace(/16/g, '40') }} />
          </div>
          <div style={{ textAlign: 'center', maxWidth: 380 }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: T.warning }}>
              Orquestrador não instalado
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
              Para conversar com um agente de IA em <strong style={{ color: T.text }}>{machine.name}</strong>, instale o orquestrador primeiro.
            </p>
          </div>
          <Button
            variant="danger"
            size="lg"
            onClick={() => { window.location.hash = `#/runner-v3/${machine.id}/detail`; }}
            style={{
              borderRadius: 14,
              background: `linear-gradient(135deg, ${T.warning}, ${T.danger})`,
            }}
          >
            Instalar Orquestrador
          </Button>
        </div>
      ) : (
        <>
          {/* Push notification prompt */}
          {pushState.value === 'prompt' && (
            <div style={{
              margin: '8px 12px', padding: '14px 16px', borderRadius: 14,
              background: `linear-gradient(135deg, ${T.primaryBgSubtle}, ${T.accentBgSubtle})`,
              border: `1px solid ${T.primaryBg}`,
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
 <div style={{ fontSize: 28, flexShrink: 0 }}></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 3 }}>
                  Ative as notificações
                </div>
                <div style={{ fontSize: 11, color: T.textSecondary, lineHeight: 1.4 }}>
                  Receba alertas quando seu agente responder, mesmo com a aba fechada
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <Button variant="ghost" size="xs" onClick={dismissPushPrompt}>Depois</Button>
                <Button variant="primary" size="xs" onClick={activatePush}>Ativar</Button>
              </div>
            </div>
          )}

          {/* ═══ MESSAGES ═══ */}
          <div
            data-chat-messages
            ref={chatAreaRef}
            onScroll={checkScroll}
            style={{
              flex: 1, overflowY: 'auto', padding: '8px 0',
              display: 'flex', flexDirection: 'column', gap: 6,
              minHeight: 0, position: 'relative',
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
          >

            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple
              style={{ display: 'none' }}
              onChange={(e: any) => { if (e.target.files) { addImages(e.target.files); sendModal.value = true; } e.target.value = ''; }}
            />
            <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx,.json,.md,.zip,.rar"
              style={{ display: 'none' }}
              onChange={async (e: any) => {
                if (!e.target.files?.length) return;
                const file = e.target.files[0];
                const dataUrl = await fileToDataUrl(file);
                pendingImages.value = [...pendingImages.value, { file, dataUrl }];
                e.target.value = '';
              }}
            />

            {loading.value ? (
              <div style={{ padding: 60, display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 500, margin: '0 auto', width: '100%' }}>
                <ListSkeleton rows={4} />
              </div>
            ) : messages.value.length === 0 ? (
              <div style={{ textAlign: 'center', color: T.textMuted, padding: 60 }}>
                <div style={{ marginBottom: 16 }} dangerouslySetInnerHTML={{ __html: IC.chat.replace(/20/g, '48') }} />
                <div style={{ fontSize: 16, fontWeight: 500 }}>Nenhuma mensagem ainda</div>
                <div style={{ fontSize: 13, marginTop: 8, opacity: 0.7 }}>Envie uma mensagem para começar.</div>
              </div>
            ) : (
              (showActions.value
                ? messages.value
                : messages.value.filter(m => m.role !== 'tool_call' && (m.role !== 'system' || m.content?.includes('Compacta')) && !isSystemNoise(m.content || ''))
              ).map((msg, i, arr) => {
                const isUser = msg.role === 'user';
                const dateLabel = formatDate(msg.timestamp);
                let showDate = false;
                if (dateLabel !== lastDate) { lastDate = dateLabel; showDate = true; }

                const isSystem = msg.role === 'system';
                const isToolCall = msg.role === 'tool_call';
                const isToolResult = msg.role === 'tool';

                let toolData: any = null;
                if ((isToolCall || isToolResult) && msg.content) {
                  try { toolData = JSON.parse(msg.content); } catch {}
                }

                return (
                  <div key={msg.id || i}>
                    {showDate && (
                      <div style={{ textAlign: 'center', margin: '14px 0 8px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, color: T.textMuted,
                          background: T.bgCard, padding: '4px 14px', borderRadius: 12,
                          border: `1px solid ${T.border}`,
                        }}>{dateLabel}</span>
                      </div>
                    )}

                    {isSystem ? (
                      <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0' }}>
                        <div style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          padding: '6px 14px', borderRadius: 16, fontSize: 12,
                          background: T.primaryBg,
                          border: `1px solid ${T.primaryBg}`,
                          color: T.primary,
                        }}>
                          {msg.content?.includes('Compacta') ? t('context_compacted') : msg.content}
                        </div>
                      </div>
                    ) : isToolCall && toolData ? (() => {
                      const meta = toolMeta(toolData.name);
                      const summary = friendlySummary(toolData);
                      const hasResult = !!toolData.result;
                      const cardId = `tc_${msg.id}`;
                      return (
                        <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0 10px', margin: '2px 0' }}>
                          <div
                            style={{
                              maxWidth: '85%', borderRadius: 12, overflow: 'hidden',
                              background: `${meta.color}08`,
                              border: `1px solid ${meta.color}18`,
                              cursor: hasResult ? 'pointer' : 'default',
                            }}
                            onClick={() => { if (!hasResult) return; const el = document.getElementById(cardId); if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}>
                              <span style={{
                                fontSize: 12, width: 24, height: 24, display: 'flex', alignItems: 'center',
                                justifyContent: 'center', borderRadius: 6, background: `${meta.color}12`,
                              }} dangerouslySetInnerHTML={{ __html: meta.icon }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                  <span style={{ fontSize: 11, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                                  {hasResult
                                    ? <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: T.successBg, color: T.success, fontWeight: 600 }}></span>
                                    : <Spinner size="sm" inline />
                                  }
                                </div>
                                {summary && (
                                  <div style={{ fontSize: 10, color: T.textMuted, fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.7 }}>
                                    {summary}
                                  </div>
                                )}
                              </div>
                              <span style={{ fontSize: 9, color: T.textMuted }}>{formatTime(msg.timestamp)}</span>
                            </div>
                            {hasResult && (
                              <div id={cardId} style={{ display: 'none' }}>
                                <div style={{ padding: '6px 12px 8px', borderTop: `1px solid ${meta.color}0a`, background: T.bgOverlay }}>
                                  <pre style={{ margin: 0, fontSize: 10, color: T.textMuted, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 120, overflow: 'auto', lineHeight: 1.5 }}>{toolData.result}</pre>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })() : isToolResult ? null : (
                      /* User / Assistant message bubble */
                      <div style={{
                        display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start',
                        padding: '0 10px',
                        animation: i >= arr.length - 3 ? 'msgIn 0.2s ease-out' : 'none',
                      }}>
                        <div style={{
                          maxWidth: '80%', padding: '10px 14px',
                          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                          background: isUser
                            ? `linear-gradient(135deg, ${T.primary}, ${T.accent})`
 : msg.content?.includes('[ WhiteLabelClaw]')
                              ? T.warningBg
                              : T.bgCardHover,
                          border: isUser ? 'none'
 : msg.content?.includes('[ WhiteLabelClaw]')
                              ? `1px solid ${T.warningBg}`
                              : `1px solid ${T.border}`,
                          color: T.text,
                        }}>
                          {/* WhiteLabelClaw platform message (e.g. insufficient balance) */}
 {!isUser && msg.content?.includes('[ WhiteLabelClaw]') ? (
                            <div style={{ fontSize: 11, color: T.warning, fontWeight: 600, marginBottom: 3 }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
                                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={T.warning} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {tenantName() || 'Platform'}
                            </div>
                          ) : !isUser && (
                            <div style={{ fontSize: 11, color: T.purple, fontWeight: 600, marginBottom: 3 }}>
                              <span dangerouslySetInnerHTML={{ __html: IC.bot }} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {activityState.value.agentName || machine.agentName}
                            </div>
                          )}

                          {/* Attachments (images + documents) */}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: msg.content ? 8 : 0 }}>
                              {msg.attachments.map((att: any, ai: number) => {
                                if (att.type === 'document' && att.fileName) {
                                  const ext = att.fileName.split('.').pop()?.toLowerCase() || '';
                                  const color = FILE_EXTENSION_COLORS[ext] || FILE_EXTENSION_DEFAULT_COLOR;
                                  return (
                                    <div key={ai} onClick={() => {
                                      try {
                                        const b64 = att.dataUrl.split(',')[1] || att.dataUrl;
                                        const binary = atob(b64);
                                        const bytes = new Uint8Array(binary.length);
                                        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                                        const blob = new Blob([bytes], { type: att.mimeType || 'application/octet-stream' });
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url; a.download = att.fileName; a.click();
                                        URL.revokeObjectURL(url);
                                      } catch (err) { console.error('Download error:', err); }
                                    }} style={{
                                      display: 'flex', alignItems: 'center', gap: 12,
                                      background: T.bgCard, border: `1px solid ${T.bgHover}`,
                                      borderRadius: 12, padding: '12px 16px', cursor: 'pointer', maxWidth: 320,
                                      transition: 'background 0.15s',
                                    }}
                                    onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgHover}
                                    onMouseLeave={(e: any) => e.currentTarget.style.background = T.bgCard}
                                    >
                                      <div style={{
                                        width: 42, height: 42, borderRadius: 10,
                                        background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                      }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke={color} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                      </div>
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{att.fileName}</div>
                                        <div style={{ fontSize: 11, color, marginTop: 2, fontWeight: 500 }}>{ext.toUpperCase()} — Toque para baixar</div>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <img key={ai} src={att.dataUrl}
                                    style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 10, objectFit: 'contain', cursor: 'pointer' }}
                                    onClick={() => { imageModal.value = att.dataUrl; }}
                                  />
                                );
                              })}
                            </div>
                          )}

                          {/* Document file cards — Telegram-style download */}
                          {(() => {
                            const fileMatches = (msg.content || '').matchAll(/\[media attached: ([^\]|]+?)(?:\s*\([^)]*\))?\s*(?:\|[^\]]*)?\]/g);
                            const files: Array<{path: string; name: string; ext: string}> = [];
                            for (const fm of fileMatches) {
                              const fp = fm[1].trim();
                              const ext = fp.split('.').pop()?.toLowerCase() || '';
                              if (['jpg','jpeg','png','gif','webp','svg','bmp'].includes(ext)) continue;
                              files.push({ path: fp, name: fp.split('/').pop() || fp, ext });
                            }
                            if (files.length === 0) return null;
                            return files.map((f, fi) => {
                              const color = FILE_EXTENSION_COLORS[f.ext] || FILE_EXTENSION_DEFAULT_COLOR;
                              return (
                                <div key={fi} onClick={async () => {
                                  const m = selectedMachine.value;
                                  if (!m) return;
                                  try {
                                    const container = m.os === 'linux' ? true : false;
                                    const cmd = container
                                      ? `docker exec $(docker ps --format '{{.Names}}' | head -1) base64 -w0 ${f.path} 2>/dev/null`
                                      : `base64 -i ${f.path} 2>/dev/null || base64 ${f.path} 2>/dev/null`;
                                    const { runCommandDirect } = await import('../lib/runCommand');
                                    const result = await runCommandDirect(m.id, cmd);
                                    if (result && result.trim()) {
                                      const mimeMap: Record<string,string> = {
                                        pdf:'application/pdf',doc:'application/msword',
                                        docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                                        xls:'application/vnd.ms-excel',xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                        zip:'application/zip',rar:'application/x-rar',txt:'text/plain',md:'text/markdown',
                                        json:'application/json',csv:'text/csv',html:'text/html',
                                      };
                                      const mime = mimeMap[f.ext] || 'application/octet-stream';
                                      const binary = atob(result.trim());
                                      const bytes = new Uint8Array(binary.length);
                                      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                                      const blob = new Blob([bytes], { type: mime });
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url; a.download = f.name; a.click();
                                      URL.revokeObjectURL(url);
                                    }
                                  } catch (err) { console.error('File download error:', err); }
                                }} style={{
                                  display: 'flex', alignItems: 'center', gap: 12,
                                  background: T.bgCard, border: `1px solid ${T.bgHover}`,
                                  borderRadius: 12, padding: '12px 16px', margin: '8px 0', cursor: 'pointer', maxWidth: 320,
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgHover}
                                onMouseLeave={(e: any) => e.currentTarget.style.background = T.bgCard}
                                >
                                  <div style={{
                                    width: 42, height: 42, borderRadius: 10,
                                    background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                  }}>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke={color} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                  </div>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</div>
                                    <div style={{ fontSize: 11, color, marginTop: 2, fontWeight: 500 }}>{f.ext.toUpperCase()} — Toque para baixar</div>
                                  </div>
                                </div>
                              );
                            });
                          })()}

                          {/* Content */}
 {msg.content && !msg.content.startsWith('[media attached:') && (
                            <div style={{ fontSize: 14, lineHeight: 1.6, wordBreak: 'break-word' }}
                              dangerouslySetInnerHTML={{ __html: renderContent(msg.content) }} />
                          )}

                          {/* Timestamp */}
                          <div style={{
                            fontSize: 10, marginTop: 4,
                            color: isUser ? T.textMuted : T.textMuted,
                            textAlign: isUser ? 'right' : 'left',
                            display: 'flex', alignItems: 'center',
                            justifyContent: isUser ? 'flex-end' : 'flex-start', gap: 3,
                          }}>
                            <span>{formatTime(msg.timestamp)}</span>
                            {isUser && <span style={{ fontSize: 11 }}>{msg.id.startsWith('opt_') ? '' : ''}</span>}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Sending indicator */}
            {sending.value && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{ padding: '10px 18px', borderRadius: '18px 18px 18px 4px', background: T.bgCardHover, border: `1px solid ${T.border}` }}>
                  <TypingIndicator />
                </div>
              </div>
            )}

            {/* Streaming response bubble — real-time text */}
            {activityState.value.status === 'streaming' && activityState.value.streamingText && (
              <div style={{
                display: 'flex', justifyContent: 'flex-start', padding: '0 10px',
                animation: 'msgIn 0.2s ease-out',
              }}>
                <div style={{
                  maxWidth: '80%', padding: '10px 14px',
                  borderRadius: '18px 18px 18px 4px',
                  background: T.bgCardHover,
                  border: `1px solid ${T.purpleBg}`,
                  color: T.text,
                }}>
                  <div style={{ fontSize: 11, color: T.purple, fontWeight: 600, marginBottom: 3 }}>
                    <span dangerouslySetInnerHTML={{ __html: IC.bot }} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> {activityState.value.agentName || machine.agentName}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.6, wordBreak: 'break-word' }}
                    dangerouslySetInnerHTML={{ __html: renderContent(activityState.value.streamingText) + '<span class="streaming-cursor">▊</span>' }} />
                </div>
              </div>
            )}

            {/* Active tool indicator — shows which integration/tool is being used */}
            {activityState.value.status === 'tool' && activityState.value.tool && (() => {
              const t = activityState.value.tool!;
              const meta = toolMeta(t.name);
              const detail = t.detail?.replace(/\/data\/\.openclaw\/workspace\//g, '') || '';
              return (
                <div style={{ display: 'flex', padding: '4px 10px' }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 14px', borderRadius: 14,
                    background: `${meta.color}0a`, border: `1px solid ${meta.color}18`,
                    maxWidth: '85%',
                  }}>
                    <span style={{
                      fontSize: 14, width: 28, height: 28, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', borderRadius: 8, background: `${meta.color}15`,
                    }} dangerouslySetInnerHTML={{ __html: meta.icon }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: meta.color }}>{meta.label}</span>
                        <Spinner size="sm" inline />
                      </div>
                      {detail && (
                        <div style={{
                          fontSize: 11, color: T.textMuted, fontFamily: "'JetBrains Mono', monospace",
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                          marginTop: 2, opacity: 0.7, maxWidth: 280,
                        }}>{detail}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Activity indicator */}
            {activityState.value.status === 'thinking' && !sending.value && (
              <div style={{ display: 'flex', padding: '4px 0' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '6px 14px', borderRadius: 16,
                  background: T.purpleBg, border: `1px solid ${T.purpleBg}`,
                }}>
                  <div style={{ display: 'flex', gap: 3 }}>
                    {[0, 0.15, 0.3].map((d, j) => (
                      <span key={j} style={{ width: 5, height: 5, borderRadius: '50%', background: T.purple, animation: `pulse 1s ease-in-out ${d}s infinite` }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 12, color: T.purple, fontWeight: 500 }}>
                    {activityState.value.detail || t('thinking')}
                  </span>
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* Scroll to bottom button — Telegram style */}
          {showScrollBtn && (
            <div style={{ position: 'absolute', bottom: 66, right: 12, zIndex: 50 }}>
              <Button variant="ghost" onClick={() => scrollToBottom()} style={{
                width: 40, height: 40, borderRadius: '50%',
                background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
                border: 'none',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 20px ${T.primaryBgStrong}`,
                transition: 'all 0.2s',
                position: 'relative',
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -8, right: -8,
                    background: T.danger, color: T.text,
                    fontSize: 10, fontWeight: 700, minWidth: 20, height: 20,
                    borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 5px', border: `2px solid ${T.bg}`,
                    boxShadow: `0 2px 8px ${T.dangerBgStrong}`,
                  }}>{unreadCount}</span>
                )}
              </Button>
            </div>
          )}

          {/* Pending images strip */}
          {pendingImages.value.length > 0 && (
            <div style={{
              padding: '8px 12px', background: T.bgCard,
              borderTop: `1px solid ${T.border}`,
              display: 'flex', gap: 8, flexShrink: 0,
            }}>
              {pendingImages.value.map((img, idx) => (
                <div key={idx} style={{ position: 'relative', flexShrink: 0 }}>
                  <img src={img.dataUrl} style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 10, border: `2px solid ${T.border}` }} />
                  <Button variant="ghost" onClick={() => { pendingImages.value = pendingImages.value.filter((_, i) => i !== idx); }}
                    style={{ position: 'absolute', top: -5, right: -5, width: 18, height: 18, borderRadius: '50%', background: T.danger, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: T.text }}>×</Button>
                </div>
              ))}
            </div>
          )}

          {/* ═══ INPUT BAR ═══ */}
          <div style={{
            padding: '10px 12px 12px', borderTop: `1px solid ${T.border}`,
            background: T.bgCard, flexShrink: 0,
          }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              {/* Attach button + popover */}
              <div ref={attachBtnRef} style={{ position: 'relative', flexShrink: 0 }}>
                <Button variant="ghost"
                  className="chat-action-btn"
                  onClick={() => { attachMenuOpen.value = !attachMenuOpen.value; }}
                  style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: attachMenuOpen.value ? T.primaryBg : 'transparent',
                    border: `1px solid ${attachMenuOpen.value ? T.primaryBg : T.bgHover}`,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, color: attachMenuOpen.value ? T.text : T.textMuted,
                    transition: 'all 0.2s',
                  }}
                ><span style={{ display: 'inline-flex', lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: IC.attach }} /></Button>

                {/* Floating menu */}
                {attachMenuOpen.value && (
                  <div style={{
                    position: 'absolute', bottom: 46, left: 0,
                    background: `linear-gradient(145deg, ${T.bgSecondary}, ${T.bg})`, borderRadius: 14,
                    boxShadow: `0 12px 40px ${T.blackAlpha50}, 0 0 0 1px ${T.border}`,
                    padding: 6, minWidth: 180, animation: 'modalSlideIn 0.15s ease-out',
                    zIndex: 50,
                  }}>
                    <Button variant="ghost"
                      className="attach-menu-item"
                      onClick={() => { attachMenuOpen.value = false; fileInputRef.current?.click(); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '10px 14px', border: 'none', borderRadius: 10,
                        background: 'transparent', color: T.text, fontSize: 13, fontWeight: 500,
                        cursor: 'pointer', fontFamily: 'Manrope, sans-serif', transition: 'all 0.12s',
                      }}>
                      <span dangerouslySetInnerHTML={{ __html: IC.img }} style={{ display: 'inline-flex' }} />
                      <span>Imagem</span>
                    </Button>
                    <Button variant="ghost"
                      className="attach-menu-item"
                      onClick={() => { attachMenuOpen.value = false; docInputRef.current?.click(); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                        padding: '10px 14px', border: 'none', borderRadius: 10,
                        background: 'transparent', color: T.text, fontSize: 13, fontWeight: 500,
                        cursor: 'pointer', fontFamily: 'Manrope, sans-serif', transition: 'all 0.12s',
                      }}>
                      <span dangerouslySetInnerHTML={{ __html: IC.doc }} style={{ display: 'inline-flex' }} />
                      <span>Documento</span>
                    </Button>
                  </div>
                )}
              </div>

              {/* Text input */}
              <Textarea
                autoResize
                maxHeight={120}
                placeholder={machine.status === 'online' ? t('type_here') : t('machine_offline')}
                value={inputText.value}
                onChange={(v) => { inputText.value = v; }}
                onKeyDown={(e: KeyboardEvent) => {
                  const isMobile = window.innerWidth <= 768 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
                  if (e.key === 'Enter' && !e.shiftKey && !isMobile) { e.preventDefault(); sendMessage(); }
                }}
                disabled={sending.value}
                rows={1}
                style={{
                  flex: 1, padding: '10px 16px', borderRadius: 22,
                  background: T.bgCard, border: `1px solid ${T.bgHover}`,
                  color: T.text, fontSize: 14, outline: 'none',
                  lineHeight: 1.5, fontFamily: 'inherit',
                }}
              />

              {/* Mic button */}
              <Button variant="ghost"
                onClick={() => { if (!isListening.value) startListening(false); else if (recognition) recognition.stop(); }}
                className="chat-action-btn"
                style={{
                  width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                  background: isListening.value && !isJarvisMode.value ? T.dangerBg : 'transparent',
                  border: `1px solid ${isListening.value && !isJarvisMode.value ? T.dangerBg : T.bgHover}`,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, color: isListening.value ? T.danger : T.textMuted,
                  transition: 'all 0.2s',
                }}
               ><span style={{ display: 'inline-flex', lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: IC.mic }} /></Button>

              {/* Jarvis mode button */}
              <Button variant="ghost"
                onClick={() => { if (isJarvisMode.value) stopListening(); else startListening(true); }}
                className="chat-action-btn"
                style={{
                  width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                  background: isJarvisMode.value ? `linear-gradient(135deg, ${T.danger}, ${T.warning})` : 'transparent',
                  border: `1px solid ${isJarvisMode.value ? 'transparent' : T.bgHover}`,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 14, color: isJarvisMode.value ? "#fff" : T.textMuted,
                  boxShadow: isJarvisMode.value ? `0 0 16px ${T.dangerBg}` : 'none',
                  transition: 'all 0.2s',
                }}
               ><span style={{ display: 'inline-flex', lineHeight: 0 }} dangerouslySetInnerHTML={{ __html: isJarvisMode.value ? IC.jarvis : IC.bot }} /></Button>

              {/* Send button */}
              <Button variant="ghost"
                data-send-btn
                onClick={sendMessage}
                className="chat-action-btn"
                disabled={(!inputText.value.trim() && pendingImages.value.length === 0) || sending.value}
                style={{
                  width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                  background: (inputText.value.trim() || pendingImages.value.length > 0) && !sending.value
                    ? `linear-gradient(135deg, ${T.primary}, ${T.accent})` : T.border,
                  border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: (inputText.value.trim() || pendingImages.value.length > 0) && !sending.value ? 1 : 0.4,
                  transition: 'all 0.2s',
                }}
              >
                <span dangerouslySetInnerHTML={{ __html: IC.send }} />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ═══ IMAGE MODAL ═══ */}
      {imageModal.value && (
        <div onClick={() => { imageModal.value = null; }} style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: T.bgOverlay,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)', cursor: 'pointer',
        }}>
          <Button variant="ghost" onClick={() => { imageModal.value = null; }} style={{
            position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderRadius: '50%',
            background: T.borderHover, border: 'none', color: T.text, fontSize: 20, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}></Button>
          <img src={imageModal.value} onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '90vw', maxHeight: '80vh', borderRadius: 12, objectFit: 'contain',
            boxShadow: `0 20px 60px ${T.bgOverlay}`, cursor: 'default',
          }} />
        </div>
      )}

      {/* ═══ SEND IMAGE MODAL ═══ */}
      {sendModal.value && pendingImages.value.length > 0 && (
        <div onClick={() => { sendModal.value = false; }} style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: 'transparent', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '90%', maxWidth: 440, borderRadius: 16, background: T.bgCard,
            border: `1px solid ${T.border}`, boxShadow: `0 20px 60px ${T.bgOverlay}`,
          }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Enviar {pendingImages.value.length > 1 ? `${pendingImages.value.length} imagens` : 'imagem'}</h3>
              <Button variant="ghost" onClick={() => { sendModal.value = false; pendingImages.value = []; }} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 18, cursor: 'pointer' }}></Button>
            </div>
            <div style={{ padding: 16, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', maxHeight: 260, overflowY: 'auto', background: T.bgOverlay }}>
              {pendingImages.value.map((img, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  <img src={img.dataUrl} style={{ width: pendingImages.value.length === 1 ? 'auto' : 100, height: pendingImages.value.length === 1 ? 'auto' : 100, maxWidth: pendingImages.value.length === 1 ? '100%' : 100, maxHeight: pendingImages.value.length === 1 ? 220 : 100, borderRadius: 8, objectFit: 'cover' }} />
                  <Button variant="ghost" onClick={() => { pendingImages.value = pendingImages.value.filter((_, i) => i !== idx); if (pendingImages.value.length === 0) sendModal.value = false; }}
                    style={{ position: 'absolute', top: -5, right: -5, width: 20, height: 20, borderRadius: '50%', background: T.danger, border: 'none', color: T.text, fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></Button>
                </div>
              ))}
            </div>
            <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Input type="text" placeholder={t("caption_optional")} value={inputText.value}
                onInput={(e: any) => { inputText.value = e.target.value; }}
                onKeyDown={(e: KeyboardEvent) => { if (e.key === 'Enter') { sendMessage(); sendModal.value = false; } }}
                autoFocus
                style={{ width: '100%', borderRadius: 12 }}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button variant="ghost" size="sm" onClick={() => { pendingImages.value = []; sendModal.value = false; inputText.value = ''; }}>Cancelar</Button>
 <Button variant="primary" size="sm" onClick={() => { sendMessage(); sendModal.value = false; }}>Enviar </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Voice transcript overlay */}
      {(isListening.value || isSpeaking.value) && (
        <div style={{
          position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)', zIndex: 60,
        }}>
          <div style={{
            padding: '10px 20px', borderRadius: 18,
            background: isJarvisMode.value ? `linear-gradient(135deg, ${T.dangerBg}, rgba(249,115,22,0.12))`
              : isSpeaking.value ? T.successBg : T.dangerBg,
            border: `1px solid ${isJarvisMode.value ? T.dangerBg : isSpeaking.value ? T.successBg : T.dangerBg}`,
            backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', gap: 10,
            boxShadow: `0 8px 32px ${T.shadowSm}`,
          }}>
            {isSpeaking.value ? (
              <>
                <span dangerouslySetInnerHTML={{ __html: IC.speaker }} />
                <span style={{ color: T.success, fontSize: 13 }}>Falando...</span>
                <Button variant="danger" size="xs" onClick={stopSpeaking} style={{ background: T.dangerBg, border: `1px solid ${T.dangerBg}` }}>Parar</Button>
              </>
            ) : (
              <>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: T.danger, animation: 'pulse 1s ease-in-out infinite' }} />
                <div>
                  <div style={{ color: isJarvisMode.value ? T.warning : T.danger, fontSize: 12, fontWeight: 600 }}>
                    {isJarvisMode.value ? 'Jarvis Mode' : t('listening')}
                  </div>
                  {voiceTranscript.value && <div style={{ color: T.text, fontSize: 13, marginTop: 2, maxWidth: 280 }}>"{voiceTranscript.value}"</div>}
                </div>
                {isJarvisMode.value && <Button variant="danger" size="xs" onClick={stopListening} style={{ background: T.dangerBg, border: `1px solid ${T.dangerBg}` }}>Desligar</Button>}
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══ DRAG OVERLAY (full screen) ═══ */}
      {dragOver.value && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 100,
          background: T.bgOverlay, border: `3px dashed ${T.primaryBgStrong}`,
          borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{ background: T.primaryBg, border: `2px dashed ${T.primaryBgStrong}`, padding: '40px 60px', borderRadius: 20, textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }} dangerouslySetInnerHTML={{ __html: IC.img.replace(/18/g, '48') }} />
            <div style={{ fontSize: 17, fontWeight: 600, color: T.text }}>Solte o arquivo aqui</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>Imagens ou documentos</div>
          </div>
        </div>
      )}

      {/* ═══ DROP MODAL (image preview + text input) ═══ */}
      {dropModalImages.value.length > 0 && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)', animation: 'modalFadeIn 0.2s ease-out',
        }} onClick={(e) => { if (e.target === e.currentTarget) { dropModalImages.value = []; dropModalText.value = ''; } }}>
          <div style={{
            background: `linear-gradient(145deg, ${T.bgSecondary}, ${T.bg})`, borderRadius: 20, padding: 28, width: '90%', maxWidth: 500,
            boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px ${T.border}, inset 0 1px 0 ${T.borderSubtle}`,
            animation: 'modalSlideIn 0.25s ease-out',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span dangerouslySetInnerHTML={{ __html: IC.img }} style={{ display: 'inline-flex', opacity: 0.7 }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Enviar imagem</span>
              </div>
              <Button variant="ghost"
                className="drop-modal-close"
                onClick={() => { dropModalImages.value = []; dropModalText.value = ''; }}
                style={{
                  width: 30, height: 30, borderRadius: '50%', border: 'none',
                  background: T.border, color: T.textMuted,
                  fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                }}></Button>
            </div>

            {/* Preview */}
            <div style={{
              display: 'flex', gap: 10, marginBottom: 20, justifyContent: 'center', flexWrap: 'wrap',
              background: T.bgOverlay, borderRadius: 14, padding: 16,
            }}>
              {dropModalImages.value.map((img, i) => (
                <div key={i} style={{ position: 'relative', borderRadius: 12, overflow: 'hidden' }}>
                  <img src={img.dataUrl} style={{
                    maxWidth: 220, maxHeight: 220, borderRadius: 12, objectFit: 'contain',
                    border: `1px solid ${T.bgHover}`, display: 'block',
                  }} />
                  <Button variant="ghost"
                    className="drop-modal-remove"
                    onClick={() => { dropModalImages.value = dropModalImages.value.filter((_, idx) => idx !== i); if (dropModalImages.value.length === 0) dropModalText.value = ''; }}
                    style={{
                      position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%',
                      background: 'transparent', backdropFilter: 'blur(10px)',
                      border: `1px solid ${T.borderHover}`, color: T.text, fontSize: 11,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.15s', opacity: 0.7,
                    }}></Button>
                </div>
              ))}
            </div>

            {/* Text input */}
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <Input
                autoFocus
                value={dropModalText.value}
                onInput={(e) => { dropModalText.value = (e.target as HTMLInputElement).value; }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    pendingImages.value = [...pendingImages.value, ...dropModalImages.value].slice(0, 4);
                    inputText.value = dropModalText.value;
                    dropModalImages.value = []; dropModalText.value = '';
                    setTimeout(() => { const btn = document.querySelector('[data-send-btn]') as HTMLButtonElement; btn?.click(); }, 50);
                  }
                }}
                placeholder={t("add_caption_optional")}
                style={{ width: '100%', borderRadius: 12 }}
              />
            </div>

            {/* Hint */}
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 16, opacity: 0.6 }}>
              Pressione <kbd style={{ background: T.bgHover, padding: '1px 6px', borderRadius: 4, fontSize: 10, border: `1px solid ${T.borderHover}` }}>Enter</kbd> para enviar · <kbd style={{ background: T.bgHover, padding: '1px 6px', borderRadius: 4, fontSize: 10, border: `1px solid ${T.borderHover}` }}>Esc</kbd> para cancelar
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => { dropModalImages.value = []; dropModalText.value = ''; }}>
                Cancelar
              </Button>
              <Button onClick={() => {
                  pendingImages.value = [...pendingImages.value, ...dropModalImages.value].slice(0, 4);
                  inputText.value = dropModalText.value;
                  dropModalImages.value = []; dropModalText.value = '';
                  setTimeout(() => { const btn = document.querySelector('[data-send-btn]') as HTMLButtonElement; btn?.click(); }, 50);
                }}>
                Enviar 
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DROP MODAL (document) ═══ */}
      {dropModalDocs.value.length > 0 && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)', animation: 'modalFadeIn 0.2s ease-out',
        }} onClick={(e) => { if (e.target === e.currentTarget) { dropModalDocs.value = []; dropModalDocText.value = ''; } }}>
          <div style={{
            background: `linear-gradient(145deg, ${T.bgSecondary}, ${T.bg})`, borderRadius: 20, padding: 28, width: '90%', maxWidth: 500,
            boxShadow: `0 24px 80px rgba(0,0,0,0.6), 0 0 0 1px ${T.border}, inset 0 1px 0 ${T.borderSubtle}`,
            animation: 'modalSlideIn 0.25s ease-out',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span dangerouslySetInnerHTML={{ __html: IC.doc }} style={{ display: 'inline-flex', opacity: 0.7 }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>Enviar documento</span>
              </div>
              <Button variant="ghost" className="drop-modal-close" onClick={() => { dropModalDocs.value = []; dropModalDocText.value = ''; }}
                style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: T.border, color: T.textMuted, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s' }}></Button>
            </div>

            {/* Doc previews */}
            <div style={{ background: T.bgOverlay, borderRadius: 14, padding: 16, marginBottom: 20 }}>
              {dropModalDocs.value.map((doc, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < dropModalDocs.value.length - 1 ? `1px solid ${T.bgCardHover}` : 'none' }}>
                  <span dangerouslySetInnerHTML={{ __html: IC.doc }} style={{ display: 'inline-flex', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{doc.file.name}</div>
                    <div style={{ fontSize: 11, color: T.textMuted }}>{(doc.file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <Button variant="ghost" className="drop-modal-remove" onClick={() => { dropModalDocs.value = dropModalDocs.value.filter((_, idx) => idx !== i); }}
                    style={{ width: 24, height: 24, borderRadius: '50%', background: T.border, border: 'none', color: T.textMuted, fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', flexShrink: 0 }}></Button>
                </div>
              ))}
            </div>

            {/* Text input */}
            <div style={{ position: 'relative', marginBottom: 16 }}>
              <Input autoFocus value={dropModalDocText.value}
                onInput={(e) => { dropModalDocText.value = (e.target as HTMLInputElement).value; }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    pendingImages.value = [...pendingImages.value, ...dropModalDocs.value].slice(0, 4);
                    inputText.value = dropModalDocText.value;
                    dropModalDocs.value = []; dropModalDocText.value = '';
                    setTimeout(() => { const btn = document.querySelector('[data-send-btn]') as HTMLButtonElement; btn?.click(); }, 50);
                  }
                }}
                placeholder={t("add_message_optional")}
                style={{ width: '100%', borderRadius: 12 }}
              />
            </div>

            {/* Hint */}
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 16, opacity: 0.6 }}>
              <kbd style={{ background: T.bgHover, padding: '1px 6px', borderRadius: 4, fontSize: 10, border: `1px solid ${T.borderHover}` }}>Enter</kbd> enviar · <kbd style={{ background: T.bgHover, padding: '1px 6px', borderRadius: 4, fontSize: 10, border: `1px solid ${T.borderHover}` }}>Esc</kbd> cancelar
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={() => { dropModalDocs.value = []; dropModalDocText.value = ''; }}>Cancelar</Button>
              <Button onClick={() => {
                pendingImages.value = [...pendingImages.value, ...dropModalDocs.value].slice(0, 4);
                inputText.value = dropModalDocText.value;
                dropModalDocs.value = []; dropModalDocText.value = '';
                setTimeout(() => { const btn = document.querySelector('[data-send-btn]') as HTMLButtonElement; btn?.click(); }, 50);
              }}>Enviar </Button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes msgIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dropdownIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cursorBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
        .streaming-cursor { color: ${T.purple}; animation: cursorBlink 0.8s ease-in-out infinite; font-size: 14px; margin-left: 1px; }
        .chat-action-btn:hover { background: ${T.primaryBg} !important; border-color: ${T.primaryBgStrong} !important; transform: scale(1.08); color: ${T.text} !important; }
        .chat-action-btn:active { transform: scale(0.95); }
        @keyframes modalFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes modalSlideIn { from { opacity: 0; transform: translateY(16px) scale(0.97); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .drop-modal-close:hover { background: ${T.dangerBg} !important; color: ${T.danger} !important; transform: scale(1.1); }
        .drop-modal-remove:hover { opacity: 1 !important; background: ${T.danger} !important; transform: scale(1.1); }
        .drop-modal-btn-cancel:hover { background: var(--t-white-alpha-8) !important; border-color: var(--t-white-alpha-15) !important; color: var(--text) !important; }
        .drop-modal-btn-send:hover { transform: translateY(-1px); box-shadow: 0 6px 20px ${T.primaryBgStrong} !important; filter: brightness(1.1); }
        .drop-modal-btn-send:active { transform: translateY(0); }
        .attach-menu-item:hover { background: ${T.primaryBg} !important; color: ${T.text} !important; }
      `}</style>
    </div>
  );
}

// ─── Machine Item (Dropdown) ─────────────────────────────────────────────────

function MachineItem({ machine, selected, onSelect }: { machine: ChatMachine; selected: boolean; onSelect: (m: ChatMachine) => void }) {
  const isOnline = machine.status === 'online';
  return (
    <Button variant="ghost"
      onClick={() => onSelect(machine)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '10px 14px', border: 'none',
        background: selected ? T.primaryBg : 'transparent',
        cursor: 'pointer', textAlign: 'left', color: T.text,
        transition: 'background 0.1s',
        opacity: isOnline ? 1 : 0.5,
      }}
      onMouseEnter={(e) => { if (!selected) (e.currentTarget as HTMLElement).style.background = T.bgCard; }}
      onMouseLeave={(e) => { if (!selected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
    >
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <span style={{ display: "inline-flex" }} dangerouslySetInnerHTML={{ __html: getOsIcon(machine.os) }} />
        <div style={{
          position: 'absolute', bottom: -1, right: -3,
          width: 8, height: 8, borderRadius: '50%',
          background: isOnline ? T.success : T.textDisabled,
          border: `2px solid ${T.bgCard}`,
        }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {machine.name}
        </div>
        <div style={{ fontSize: 11, color: T.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span>{getOsLabel(machine.os)}</span>
          {machine.hasOpenclaw && (
            <>
              <span style={{ opacity: 0.4 }}> · </span>
              <span style={{ color: T.purple, fontSize: 10 }}>Agente IA</span>
            </>
          )}
          {machine.agentModel && (
            <>
              <span style={{ opacity: 0.4 }}> · </span>
              <span style={{ color: T.accent, fontSize: 10 }}>
                {machine.agentModel.includes('opus') ? 'Opus' :
                 machine.agentModel.includes('sonnet') ? 'Sonnet' :
                 machine.agentModel.includes('haiku') ? 'Haiku' :
                 machine.agentModel.includes('gpt-4') ? 'GPT-4' :
                 machine.agentModel.includes('gpt-3') ? 'GPT-3.5' :
                 machine.agentModel.split('/').pop()}
              </span>
            </>
          )}
          {!isOnline && (
            <>
              <span style={{ opacity: 0.4 }}> · </span>
              <span>{formatTimeAgo(machine.lastSeen)}</span>
            </>
          )}
        </div>
      </div>
      {selected && <span style={{ color: T.primary, fontSize: 14 }}></span>}
    </Button>
  );
}
